import { ShieldCheck, ShieldAlert, Info } from "lucide-react";

/**
 * Phase C · Etappe 2 · F5b Warn-Banner (Gefahrstoffmodul, 22.08.2026)
 *
 * Zeigt fail-visible den SDB-Verwendungsstatus einer Welt. Kein Rendering
 * bei Nicht-Hazmat-Welten (course.hazmat_sdb_status == null).
 *
 * Statusfarben:
 *  - "archived"        → grün (SDB wurde verwendet, Beleg-Nummer angezeigt)
 *  - "not_found"       → amber ("Kein SDB gefunden — Welt aus Fachwissen")
 *  - "uncertain"       → amber ("Mehrere Treffer, keine automatische Auswahl")
 *  - "unreadable"      → amber ("SDB gefunden, aber PDF nicht lesbar — kein OCR")
 */
export const HazmatSdbStatusBanner = ({ status, documentNumber, sourceUrl }) => {
  if (!status) return null;

  const isArchived = status === "archived";
  const bgClass = isArchived
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : "border-amber-200 bg-amber-50 text-amber-900";
  const Icon = isArchived ? ShieldCheck : ShieldAlert;

  const messages = {
    archived: (
      <>
        <strong>Sicherheitsdatenblatt verwendet.</strong>{" "}
        Diese Betriebsanweisung basiert auf einem strukturell ausgelesenen SDB.
        {documentNumber && <> (Belegnummer <code className="text-xs font-mono">{documentNumber}</code>)</>}
        {sourceUrl && (
          <>
            {" — "}
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
              data-testid="hazmat-sdb-status-source-link"
            >
              Quelle ansehen
            </a>
          </>
        )}
      </>
    ),
    not_found: (
      <>
        <strong>Kein Sicherheitsdatenblatt gefunden.</strong>{" "}
        Die Betriebsanweisung wurde aus konservativem Fachwissen erstellt. Bitte
        das Original-SDB des Herstellers zur Kontrolle heranziehen.
      </>
    ),
    uncertain: (
      <>
        <strong>Mehrere gleichrangige SDB-Treffer gefunden.</strong>{" "}
        Keine automatische Auswahl möglich — die Betriebsanweisung wurde ohne
        SDB-Bindung erstellt. Bitte das Produkt-SDB manuell prüfen.
      </>
    ),
    unreadable: (
      <>
        <strong>SDB gefunden, aber nicht lesbar.</strong>{" "}
        Das gefundene PDF konnte nicht strukturell ausgelesen werden (kein OCR).
        Die Betriebsanweisung wurde ohne SDB-Bindung erstellt.
      </>
    ),
  };

  const content = messages[status] || (
    <>
      <strong>SDB-Status: {status}</strong>
    </>
  );

  return (
    <div
      data-testid={`hazmat-sdb-status-banner-${status}`}
      className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm mb-4 ${bgClass}`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>{content}</div>
    </div>
  );
};

/**
 * Phase C · Etappe 2 · F4 Feld-Prefix-Badge (Gefahrstoffmodul, 22.08.2026,
 * gehärtet 23.08.2026 auf satzweise Erkennung — MP-3.7).
 *
 * Erkennt den Prefix `[Nicht aus SDB]` in Text-Feldern der Betriebsanweisung
 * und rendert ein kleines slate-Badge davor. Ab MP-3.7 setzt die KI den
 * Prefix satzweise (nicht nur am Anfang des ganzen Texts), deshalb splittet
 * dieser Renderer den Text an jedem `[Nicht aus SDB]`-Vorkommen und rendert
 * jedes zugehörige Segment mit einem eigenen Badge. Rückwärtskompatibel:
 * Prefix nur am Anfang (Alt-Verhalten) funktioniert weiterhin identisch.
 *
 * Verwendung:
 *   <NichtAusSdbText text={item.description} />
 * oder um jeden Listenpunkt zu wrappen:
 *   {items.map((it, i) => <NichtAusSdbText key={i} text={it} />)}
 */
const _PREFIX = "[Nicht aus SDB]";

export const NichtAusSdbText = ({ text, className = "" }) => {
  if (typeof text !== "string" || !text) return null;
  if (!text.includes(_PREFIX)) {
    return <span className={className}>{text}</span>;
  }
  // Split an jedem Prefix-Vorkommen. Erstes Segment (vor dem ersten Prefix)
  // wird ohne Badge angezeigt (SDB-gedeckt); jedes weitere Segment beginnt
  // mit einem Badge.
  const parts = text.split(_PREFIX);
  const leading = parts[0].trim();
  const suffixSegments = parts.slice(1).map((s) => s.trim()).filter((s) => s);
  return (
    <span className={className}>
      {leading && <span>{leading}{suffixSegments.length ? " " : ""}</span>}
      {suffixSegments.map((seg, i) => (
        <span key={i}>
          <span
            data-testid="nicht-aus-sdb-badge"
            className="inline-flex items-center gap-1 rounded-sm bg-slate-200 text-slate-700 text-[10px] uppercase font-semibold px-1.5 py-0.5 mr-1 align-baseline"
            title="Dieser Punkt stammt nicht aus dem Sicherheitsdatenblatt"
          >
            <Info className="w-2.5 h-2.5" />
            Nicht aus SDB
          </span>
          {seg}
          {i < suffixSegments.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
};
